import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown, ChevronRight, Eye, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DocumentViewerDrawer } from '../../../DocumentViewer/DocumentViewerDrawer';
import { AmendmentBadge } from '../../../v2/amendment/AmendmentBadge';
import { groupByLogicalKey } from '../../../v2/amendment/groupByLogicalKey';
/**
 * FactSection — a titled group of dense fact rows used across all views.
 *
 * Deduplicates facts by logicalKey using the shared groupByLogicalKey
 * helper — only the current version of each logical group is shown.
 *
 * Each expanded fact row has a "View in document" button that opens the
 * DocumentViewerDrawer with the source PDF page rendered and the exact
 * block highlighted with a bounding box. This is the primary trust
 * mechanism — users verify AI extractions against the original document.
 */ export function FactSection({ label, facts, titleFn, metaFn, accent = 'slate', projectId }) {
    const [expandedIdx, setExpandedIdx] = useState(null);
    const [viewerDoc, setViewerDoc] = useState(null);
    const [viewerBlockId, setViewerBlockId] = useState();
    const [viewerPage, setViewerPage] = useState();
    // Dedup by logicalKey — only show the current version of each group.
    const current = useMemo(()=>groupByLogicalKey(facts).currentList, [
        facts
    ]);
    if (current.length === 0) return null;
    const borderClass = {
        red: 'border-l-red-400',
        amber: 'border-l-amber-400',
        blue: 'border-l-blue-400',
        purple: 'border-l-purple-400',
        green: 'border-l-emerald-400',
        slate: 'border-l-slate-300'
    };
    return /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-1.5 flex items-baseline gap-2",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-[11px] font-bold uppercase tracking-wider text-slate-500",
                        children: label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-[10px] text-slate-400",
                        children: current.length
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white",
                children: current.map((fact, i)=>{
                    const isExpanded = expandedIdx === i;
                    const title = titleFn(fact);
                    const meta = metaFn?.(fact);
                    const displayTitle = title.length > 120 ? `${title.slice(0, 117)}...` : title;
                    const confidenceDot = getConfidenceDot(fact.confidence);
                    return /*#__PURE__*/ _jsxs("div", {
                        className: `border-l-2 ${borderClass[accent] || borderClass.slate}`,
                        children: [
                            /*#__PURE__*/ _jsxs("button", {
                                type: "button",
                                onClick: ()=>setExpandedIdx(isExpanded ? null : i),
                                className: "flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-slate-50",
                                children: [
                                    isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                        className: "h-3 w-3 flex-shrink-0 text-slate-400"
                                    }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                        className: "h-3 w-3 flex-shrink-0 text-slate-400"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "min-w-0 flex-1 truncate text-[13px] font-medium text-slate-800",
                                        children: displayTitle
                                    }),
                                    meta && /*#__PURE__*/ _jsx("span", {
                                        className: "hidden flex-shrink-0 text-[11px] text-slate-500 sm:inline",
                                        children: meta
                                    }),
                                    fact.amendmentNumber && /*#__PURE__*/ _jsx(AmendmentBadge, {
                                        amendmentNumber: fact.amendmentNumber,
                                        isCurrent: fact.isCurrentVersion,
                                        size: "sm"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `h-2 w-2 flex-shrink-0 rounded-full ${confidenceDot}`,
                                        title: `${Math.round((fact.confidence ?? 1) * 100)}% confidence`
                                    })
                                ]
                            }),
                            isExpanded && /*#__PURE__*/ _jsxs("div", {
                                className: "border-t border-slate-100 bg-slate-50/60 px-8 py-3",
                                children: [
                                    fact.sourceText && /*#__PURE__*/ _jsx("div", {
                                        className: "mb-2 rounded-md border-l-2 border-slate-300 bg-white px-3 py-2",
                                        children: /*#__PURE__*/ _jsxs("p", {
                                            className: "text-[12px] italic leading-relaxed text-slate-600",
                                            children: [
                                                "“",
                                                fact.sourceText,
                                                "”"
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-center gap-3 text-[11px] text-slate-500",
                                        children: [
                                            fact.sourceFilename && /*#__PURE__*/ _jsxs("span", {
                                                className: "flex items-center gap-1",
                                                children: [
                                                    /*#__PURE__*/ _jsx(FileText, {
                                                        className: "h-3 w-3"
                                                    }),
                                                    fact.sourceFilename.length > 40 ? `${fact.sourceFilename.slice(0, 37)}...` : fact.sourceFilename
                                                ]
                                            }),
                                            fact.sourcePageNumbers && fact.sourcePageNumbers.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    "p. ",
                                                    fact.sourcePageNumbers.join(', ')
                                                ]
                                            }),
                                            fact.sourceSectionNumbers && fact.sourceSectionNumbers.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    "§ ",
                                                    fact.sourceSectionNumbers.join(', ')
                                                ]
                                            }),
                                            fact.sourceDocumentId && fact.sourceBlockIds && fact.sourceBlockIds.length > 0 && /*#__PURE__*/ _jsxs("button", {
                                                type: "button",
                                                onClick: (e)=>{
                                                    e.stopPropagation();
                                                    setViewerDoc({
                                                        documentId: fact.sourceDocumentId ?? '',
                                                        filename: fact.sourceFilename || 'Source Document'
                                                    });
                                                    setViewerBlockId((fact.sourceBlockIds ?? [])[0]);
                                                    setViewerPage(fact.sourcePageNumbers?.[0]);
                                                },
                                                className: "flex items-center gap-1 font-medium text-blue-600 hover:text-blue-800 transition-colors",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Eye, {
                                                        className: "h-3 w-3"
                                                    }),
                                                    "View in document"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }, i);
                })
            }),
            /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: !!viewerDoc,
                onClose: ()=>setViewerDoc(null),
                projectId: projectId,
                document: viewerDoc,
                highlightBlockId: viewerBlockId,
                initialPage: viewerPage
            })
        ]
    });
}
function getConfidenceDot(confidence) {
    const c = confidence ?? 1;
    if (c >= 0.8) return 'bg-emerald-400';
    if (c >= 0.5) return 'bg-amber-400';
    return 'bg-red-400';
}
